import type { Pessoa } from '@sheafyield/dominio';
import type { Banco } from '@sheafyield/sync';
import { useId } from 'react';

import { paraVisitarHoje } from '../dados/consultas.js';
import { useConsultaCom, type MotorObservavel } from '../dados/useConsulta.js';
import { Linha, type PropsLinha } from '../ds/Linha.js';
import { haQuantoTempo, horaLocal } from './relogio.js';

export interface DepsDaTira {
  banco: Banco;
  motor: MotorObservavel;
  /** O dia local corrente, em ISO com `Z` — o `de` de `limitesDoDia`. Vem
   *  de fora, e não de um `useDiaLocal()` daqui, porque a tela Hoje tem três
   *  blocos que recortam o mesmo dia: três chamadas independentes eram três
   *  temporizadores até a meia-noite e três ouvintes de `visibilitychange`
   *  para o mesmo valor. Quem o mantém é a `HojeTelaCom`. */
  dia: string;
  /** Tocar numa linha abre a folha Registrar **já ligada àquela ficha**.
   *  Injetado porque quem o resolve é a rota (`/hoje?registrar=1&pessoa=…`),
   *  como o botão largo da tela. */
  aoRegistrarPessoa: (id: string) => void;
}

/**
 * Uma pessoa virando linha.
 *
 * O título é como ela se reconhece na rua, nesta ordem: o nome, o apelido, o
 * endereço. Nenhum dos três é obrigatório no schema — uma visita a uma casa
 * pode ter criado a pessoa sem nada disso —, e o último recurso é dizer que
 * não há nome, nunca uma linha em branco.
 *
 * O endereço só vira segunda linha quando **não** é ele o título: repetido,
 * seria a mesma frase duas vezes, e em AX5 isso é meia tela.
 *
 * O detalhe reúne o agendamento e o endereço; a etiqueta à direita identifica
 * revisita ou estudo sem depender apenas de cor. A ordem continua sendo da
 * visita mais atrasada para a mais recente.
 */
function propsDaLinha(p: Pessoa, agora: Date): PropsLinha {
  const endereco = p.endereco.trim();
  const titulo = p.nome.trim() || p.apelido.trim() || endereco || 'Sem nome';
  const agendamento =
    p.proxima_visita_em === null
      ? ''
      : `${haQuantoTempo(p.proxima_visita_em, agora)}, às ${horaLocal(p.proxima_visita_em)}`;
  const detalhe = [agendamento, endereco !== titulo ? endereco : '']
    .filter(Boolean)
    .join(' · ');
  return {
    titulo,
    ...(detalhe ? { detalhe } : {}),
    ...(p.status === 'revisita' || p.status === 'estudo'
      ? {
          meta: (
            <span className="hoje__etiqueta" data-status={p.status}>
              {p.status === 'revisita' ? '↻ Revisita' : '✓ Estudo'}
            </span>
          ),
        }
      : {}),
  };
}

/**
 * A tira "Para visitar hoje": quem tem `proxima_visita_em` até o fim do dia,
 * incluindo os atrasados, do mais atrasado ao mais recente.
 *
 * **Sem ninguém, a tira não existe** — nem título, nem estado vazio. Um dia
 * sem visitas agendadas é o dia normal de quem sai batendo porta; um bloco
 * dizendo "ninguém agendado" ocuparia o topo da tela para não informar nada,
 * e a tela Hoje tem um estado vazio só, o das visitas do dia, que é o que de
 * fato se espera preencher.
 *
 * **Tocar numa linha abre a folha Registrar ligada àquela ficha**, e é isso
 * que dá vazão à tira. Sem esse toque ela só crescia: a consulta inclui todo
 * o atraso (é o certo), mas a única coisa que limpava a `proxima_visita_em`
 * era registrar pelo caminho longo, com o nome digitado exatamente igual ao
 * da ficha. Pelos dois toques — o fluxo que a etapa existe para dar — a ficha
 * nem era tocada, e a tira ia empilhando no topo da tela de quem usa AX5.
 * Ligada à ficha, a visita fecha o compromisso do dia: registrar limpa a data
 * vencida, ou grava a nova que os detalhes disserem.
 *
 * O botão largo continua sendo o caminho de dois toques sem ninguém em
 * particular; a linha é o caminho de dois toques com alguém. Abrir a ficha da
 * pessoa continua sendo de outra etapa.
 */
export function TiraParaVisitar({
  banco,
  motor,
  dia,
  aoRegistrarPessoa,
}: DepsDaTira) {
  const idDoTitulo = useId();
  const { dados: pessoas, erro } = useConsultaCom(
    motor,
    `para-visitar-hoje-${dia}`,
    () => paraVisitarHoje(banco),
  );

  // Erro só aparece quando não há nada lido para mostrar: com dados na tela,
  // uma falha passageira não pode substituí-los por um recado de erro.
  if (erro !== null && pessoas === undefined) {
    return (
      <p className="hoje__erro" role="alert">
        Não deu para ler quem visitar hoje: {erro.message}
      </p>
    );
  }
  // `undefined` é "a leitura ainda não voltou": nada na tela até saber.
  if (pessoas === undefined || pessoas.length === 0) return null;

  const agora = new Date();
  return (
    <section className="hoje__secao" aria-labelledby={idDoTitulo}>
      <h2 className="hoje__titulo" id={idDoTitulo}>
        Para visitar hoje
      </h2>
      <ul className="hoje__lista">
        {pessoas.map((p) => (
          <Linha
            key={p.id}
            {...propsDaLinha(p, agora)}
            aoTocar={() => {
              aoRegistrarPessoa(p.id);
            }}
          />
        ))}
      </ul>
    </section>
  );
}
